// Оружие на полу (трофеи с убитых и подборы уровня): та же GLB-модель,
// парит и вращается вокруг центра, ярко подсвечено — пульсирующее
// свечение модели, световое пятно на полу/стенах (точечный свет) и ореол
// из искр. Видно издалека и в темноте.

import type { MeshInstance, PointLight, Renderer } from "../engine/renderer";
import type { ParticleSimulation } from "../engine/particles/simulate";
import type { EmitterConfig } from "../engine/particles/types";
import type { PickupInit } from "../net/protocol";
import { WeaponId, weaponDef, weaponFromPickupType } from "./catalog";
import type { WeaponAssets } from "./assets";
import { GLTF_TO_WORLD, m4, m4Mul, m4Trs } from "./mat";

/** Сколько ближайших подборов получают точечный свет (у шейдеров ≤ 8 ламп). */
const MAX_GLOW_LIGHTS = 3;
const SPIN_RPS = 0.45;
const BOB_AMP = 0.06;

function glowPreset(): EmitterConfig {
  return {
    visual: "fire", blend: "additive", rate: 0,
    lifetimeMin: 0.9, lifetimeMax: 1.6,
    speedMin: 0.05, speedMax: 0.25,
    coneAngleRad: 0.6, emitDir: [0, 0, 1],
    gravityZ: -0.35, drag: 1.5, turbulence: 0.4,
    baseSize: 0.16,
    sizeOverLife: [{ t: 0, v: 0.3 }, { t: 0.3, v: 1 }, { t: 1, v: 0.1 }],
    alphaOverLife: [{ t: 0, v: 0 }, { t: 0.2, v: 0.8 }, { t: 1, v: 0 }],
    offset: [0, 0, 0], enabled: false,
  };
}

function sparklePreset(): EmitterConfig {
  return {
    visual: "spark", blend: "additive", rate: 0,
    lifetimeMin: 0.6, lifetimeMax: 1.2,
    speedMin: 0.15, speedMax: 0.5,
    coneAngleRad: 0.35, emitDir: [0, 0, 1],
    gravityZ: -0.2, drag: 0.8, turbulence: 0.6,
    baseSize: 0.05,
    sizeOverLife: [{ t: 0, v: 1 }, { t: 1, v: 0.2 }],
    alphaOverLife: [{ t: 0, v: 1 }, { t: 1, v: 0 }],
    offset: [0, 0, 0], enabled: false,
  };
}

interface Vis {
  inst: MeshInstance;
  weapon: number;
  phase: number;
  carry: number;
}

export class WeaponPickups {
  private vis = new Map<number, Vis>();
  private glowId: number;
  private sparkId: number;
  private tmpA = m4();
  private tmpB = m4();
  private timeSec = 0;
  private near: { x: number; y: number; z: number; w: number; d: number }[] = [];

  constructor(private renderer: Renderer, private particles: ParticleSimulation, private assets: WeaponAssets) {
    this.glowId = particles.registerEmitter(glowPreset(), { x: 0, y: 0, z: 0 });
    this.sparkId = particles.registerEmitter(sparklePreset(), { x: 0, y: 0, z: 0 });
  }

  /** Тип pickup'а — оружие (его рисует этот модуль, а не общий код). */
  static isWeapon(type: number): boolean {
    return weaponFromPickupType(type) >= 0;
  }

  /** Кадр: модели, вращение, свечение, искры. */
  update(pickups: Map<number, PickupInit>, dt: number, eye: { x: number; y: number; z: number }, lite: boolean): void {
    this.timeSec += dt;
    const seen = new Set<number>();
    this.near.length = 0;
    const t = this.renderer.getTimeAccSec();
    for (const [id, p] of pickups) {
      const w = weaponFromPickupType(p.type);
      if (w < 0 || p.state !== 0 || w === WeaponId.Fists) continue;
      const mesh = this.assets.weaponMesh(w);
      if (!mesh) continue;
      seen.add(id);
      let v = this.vis.get(id);
      if (!v || v.inst.mesh !== mesh) {
        if (v) this.renderer.removeMeshInstance(v.inst);
        const inst = this.renderer.createMeshInstance({ mesh, x: 0, y: 0, z: 0, yaw: 0, scale: 1, initialAnim: "idle" });
        inst.worldMatrix = m4();
        this.renderer.addMeshInstance(inst);
        v = { inst, weapon: w, phase: (id * 1.37) % (Math.PI * 2), carry: 0 };
        this.vis.set(id, v);
      }
      // Вращение вокруг центра габарита, лёгкое покачивание вверх-вниз.
      const pts = this.assets.pts(w);
      const cx = pts ? (pts.bbox_min[0] + pts.bbox_max[0]) / 2 : 0.3;
      const cy = pts ? (pts.bbox_min[1] + pts.bbox_max[1]) / 2 : 0;
      const cz = pts ? (pts.bbox_min[2] + pts.bbox_max[2]) / 2 : 0;
      const yaw = p.yaw + this.timeSec * SPIN_RPS * Math.PI * 2 + v.phase;
      const bob = Math.sin(this.timeSec * 2.2 + v.phase) * BOB_AMP;
      const big = w === WeaponId.Grenade ? 1.6 : 1;
      const z = p.z + 0.25 + bob;
      m4Trs(this.tmpA, p.x, p.y, z, 0, -0.18, yaw, big);
      m4Trs(this.tmpB, -cx, -cy, -cz, 0, 0, 0, 1);
      m4Mul(this.tmpA, this.tmpA, this.tmpB);
      m4Mul(v.inst.worldMatrix!, this.tmpA, GLTF_TO_WORLD);
      v.inst.worldAabbDirty = true;
      // Пульсирующее свечение цветом вида оружия.
      const g = weaponDef(w).glow;
      const pulse = 0.5 + 0.5 * Math.sin(this.timeSec * 4.0 + v.phase);
      const k = 0.7 + 1.1 * pulse;
      v.inst.emissive = [g[0] * k, g[1] * k, g[2] * k];
      // Ореол: тёплые сгустки и искры поднимаются от предмета.
      v.carry += dt * (lite ? 8 : 20);
      while (v.carry >= 1) {
        v.carry -= 1;
        const a = Math.random() * Math.PI * 2;
        const r = 0.18 + Math.random() * 0.2;
        if (Math.random() < 0.55) {
          this.particles.emitBurst(this.glowId, p.x + Math.cos(a) * r, p.y + Math.sin(a) * r, p.z - 0.25 + Math.random() * 0.15, t, 1, [0, 0, 1], 0.02);
        } else {
          this.particles.emitBurst(this.sparkId, p.x + Math.cos(a) * r * 0.6, p.y + Math.sin(a) * r * 0.6, z, t, 1, [0, 0, 1], 0.05);
        }
      }
      const d = Math.hypot(p.x - eye.x, p.y - eye.y, p.z - eye.z);
      this.near.push({ x: p.x, y: p.y, z: p.z + 0.15, w, d });
    }
    for (const [id, v] of this.vis) {
      if (!seen.has(id)) {
        this.renderer.removeMeshInstance(v.inst);
        this.vis.delete(id);
      }
    }
    this.near.sort((a, b) => a.d - b.d);
  }

  /** Световые пятна ближайших подборов (без «плафона»). */
  appendLights(out: PointLight[]): void {
    const n = Math.min(MAX_GLOW_LIGHTS, this.near.length);
    for (let i = 0; i < n; i++) {
      const q = this.near[i]!;
      const g = weaponDef(q.w).glow;
      const pulse = 0.65 + 0.35 * Math.sin(this.timeSec * 4.0 + i);
      out.push({
        x: q.x, y: q.y, z: q.z + 0.35,
        range: 5.0,
        color: [g[0] * 2.6 * pulse, g[1] * 2.6 * pulse, g[2] * 2.6 * pulse],
        noBulb: true,
      });
    }
  }

  clear(): void {
    for (const v of this.vis.values()) this.renderer.removeMeshInstance(v.inst);
    this.vis.clear();
    this.near.length = 0;
  }
}
